import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Screen } from '@/components/layout/Screen';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useCBSBatchStore } from '@/stores/cbsBatchStore';
import { Syringe, Check, Calendar, AlertCircle, Camera, Loader2 } from 'lucide-react';
import { getAuthenticatedClient } from '@/services/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useInlineCamera } from '@/hooks/useInlineCamera';
import {
  NoBatchPlaceholder,
  VaccineTypePicker,
  VaccineMethodPicker,
  type VaccineType,
  type VaccineMethod,
} from '@/components/cbs';
import { useSimpleMode } from '@/contexts/SimpleModeContext';
import { SimpleMetric } from '@/components/cbs/SimpleMetric';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

const vaccineTypes: VaccineType[] = [
  { id: 'newcastle', emoji: '💉', days: [7, 18], color: 'bg-blue-500' },
  { id: 'gumboro', emoji: '💊', days: [14, 21], color: 'bg-purple-500' },
  { id: 'bronchitis', emoji: '🩺', days: [7], color: 'bg-green-500' },
  { id: 'lasota', emoji: '💧', days: [21], color: 'bg-cyan-500' },
];

const methods: VaccineMethod[] = [
  { id: 'drinking_water', emoji: '💧' },
  { id: 'eye_drop', emoji: '👁️' },
  { id: 'injection', emoji: '💉' },
  { id: 'spray', emoji: '🌫️' },
];

export const VaccinationLogScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { activeBatch } = useCBSBatchStore();
  const { isSimpleMode } = useSimpleMode();

  const [selectedVaccine, setSelectedVaccine] = useState<string | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [completedToday, setCompletedToday] = useState<string[]>([]);
  const [photoTaken, setPhotoTaken] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const inlineCamera = useInlineCamera('birds');

  const queryClient = useQueryClient();
  const vaccinationMutation = useMutation({
    mutationFn: async (input: { vaccine_name: string; administration_route: string }) => {
      const client = getAuthenticatedClient();
      const currentDay = Math.max(1, Math.floor((Date.now() - new Date(activeBatch!.placement_date).getTime()) / 86400000) + 1);
      const vaccineType = vaccineTypes.find(v => v.id === input.vaccine_name);
      const scheduledDay = vaccineType?.days.find(d => Math.abs(d - currentDay) <= 3) ?? null;

      const { error } = await client
        .from('vaccination_records')
        .insert({
          batch_id: activeBatch!.id,
          user_id: activeBatch!.user_id,
          country_id: activeBatch!.country_id,
          vaccination_date: new Date().toISOString().split('T')[0],
          batch_day: currentDay,
          vaccine_name: input.vaccine_name,
          administration_route: input.administration_route,
          birds_vaccinated: activeBatch!.current_bird_count ?? activeBatch!.doc_count,
          is_scheduled: scheduledDay !== null,
          scheduled_day: scheduledDay,
          deviation_days: scheduledDay ? currentDay - scheduledDay : null,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cbs', 'vaccinations'] });
      queryClient.invalidateQueries({ queryKey: ['cbs', 'today-task-status'] });
    },
  });

  if (!activeBatch) {
    return (
      <Screen topBarProps={{ title: isSimpleMode ? '\uD83D\uDC89' : t('cbs.vaccination.title', { defaultValue: '\uD83D\uDC89 Vaccination Log' }), showBack: true, showMapPin: false, showSettings: false }} showBottomNav={false}>
        <NoBatchPlaceholder icon={Syringe} />
      </Screen>
    );
  }

  const currentDay = Math.max(1, Math.floor((Date.now() - new Date(activeBatch.placement_date).getTime()) / 86400000) + 1);
  const recommendedVaccines = vaccineTypes.filter((v) => v.days.includes(currentDay));
  const hasRecommendation = recommendedVaccines.length > 0;

  const handleSubmit = async () => {
    if (!selectedVaccine || !selectedMethod) {
      toast({ title: t('cbs.vaccination.selectBoth'), variant: 'destructive' });
      return;
    }

    try {
      await vaccinationMutation.mutateAsync({ vaccine_name: selectedVaccine, administration_route: selectedMethod });
      setCompletedToday((prev) => [...prev, selectedVaccine]);
      toast({ title: '✅ ' + t('cbs.vaccination.saved'), description: t('cbs.vaccination.savedDesc') });

      if (recommendedVaccines.length > 1 && !completedToday.includes(selectedVaccine)) {
        setSelectedVaccine(null);
        setSelectedMethod(null);
        setPhotoTaken(false);
      } else {
        setShowSuccess(true);
        if ('vibrate' in navigator) navigator.vibrate([50, 50, 100]);
        setTimeout(() => navigate('/'), 1500);
      }
    } catch (error) {
      toast({ title: t('common.error'), variant: 'destructive' });
    }
  };

  return (
    <Screen
      topBarProps={{
        title: isSimpleMode ? '\u{1F489}' : t('cbs.vaccination.title', { defaultValue: '\u{1F489} Vaccination Log' }),
        showBack: true,
        showMapPin: false,
        showSettings: false,
        onBack: () => navigate('/'),
      }}
      showBottomNav={false}
    >
      <div className="p-4 space-y-6">
        {/* Today's Schedule Alert */}
        {hasRecommendation && (
          <Card className={cn("p-4 bg-warning/10 border-warning/30", isSimpleMode && "p-5")}>
            <div className="flex items-center gap-3">
              {isSimpleMode ? (
                <span className="text-4xl">⚠️</span>
              ) : (
                <AlertCircle className="w-8 h-8 text-warning flex-shrink-0" />
              )}
              <div>
                {!isSimpleMode && <p className="font-bold text-foreground">{t('cbs.vaccination.dueToday')}</p>}
                <div className="flex gap-2 mt-1">
                  {recommendedVaccines.map((v) => (
                    <span key={v.id} className={cn(isSimpleMode ? "text-4xl" : "text-2xl")}>
                      {v.emoji}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Current Day Display */}
        {isSimpleMode ? (
          <SimpleMetric emoji="📅" value={`Day ${currentDay}`} size="lg" color="info" />
        ) : (
          <Card className="p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <Calendar className="w-6 h-6 text-primary" />
              <span className="text-xl font-bold">
                {t('cbs.batch.day')} {currentDay}
              </span>
            </div>
          </Card>
        )}

        {/* Vaccine Selection */}
        <VaccineTypePicker
          vaccines={vaccineTypes}
          currentDay={currentDay}
          selectedVaccine={selectedVaccine}
          completedToday={completedToday}
          onSelect={setSelectedVaccine}
        />

        {/* Method Selection */}
        {selectedVaccine && (
          <VaccineMethodPicker
            methods={methods}
            selectedMethod={selectedMethod}
            onSelect={setSelectedMethod}
          />
        )}

        {/* Photo Capture */}
        {selectedVaccine && selectedMethod && (
          <Card className={cn("p-4", !inlineCamera.photoTaken && "border-warning")}>
            <input
              ref={inlineCamera.fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={inlineCamera.handleFileSelected}
            />
            <Button
              variant={inlineCamera.photoTaken ? "default" : "outline"}
              className={cn("w-full gap-3", isSimpleMode ? "h-20 text-2xl" : "h-16 text-lg")}
              onClick={inlineCamera.openCamera}
              disabled={inlineCamera.isUploading}
            >
              {inlineCamera.isUploading ? (
                <><Loader2 className="w-6 h-6 animate-spin" />{!isSimpleMode && t("common.uploading", { defaultValue: "Uploading..." })}</>
              ) : inlineCamera.photoTaken ? (
                <>{isSimpleMode ? '✅ 📸' : <><Check className="w-6 h-6" />{t("camera.photoTaken", { defaultValue: "Photo taken" })}</>}</>
              ) : (
                <>{isSimpleMode ? '📸' : <><Camera className="w-6 h-6" />{t("cbs.vaccination.takePhoto", { defaultValue: "Take vaccination photo" })}</>}</>
              )}
            </Button>
            {!inlineCamera.photoTaken && !isSimpleMode && (
              <p className="text-xs text-warning text-center mt-2">{t("cbs.vaccination.photoRequired", { defaultValue: "Photo required before saving" })}</p>
            )}
          </Card>
        )}

        {/* No Vaccination Today */}
        <Button
          variant="outline"
          className={cn("w-full", isSimpleMode ? "h-16 text-xl" : "h-14 text-lg")}
          onClick={() => navigate('/')}
        >
          ⏭️ {!isSimpleMode && t('cbs.vaccination.noVaccineToday')}
        </Button>

        {/* Submit */}
        {selectedVaccine && selectedMethod && (
          <Button
            className={cn("w-full gap-2", isSimpleMode ? "h-20 text-2xl" : "h-16 text-xl")}
            onClick={handleSubmit}
            disabled={vaccinationMutation.isPending || !inlineCamera.photoTaken}
          >
            {vaccinationMutation.isPending ? (
              <><span className="animate-spin">⏳</span> {!isSimpleMode && t('common.saving')}</>
            ) : (
              <>
                {isSimpleMode ? '✅' : <Check className="w-6 h-6" />}
                {!isSimpleMode && t('common.save')}
              </>
            )}
          </Button>
        )}
      </div>

      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/90 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
              className="text-8xl"
            >
              🎉
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-4 text-xl font-semibold text-foreground"
            >
              {t("common.saved", { defaultValue: "Saved!" })}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </Screen>
  );
};

export default VaccinationLogScreen;
